import * as React from 'react';
import { View, Text, Pressable } from 'react-native';
import Modal from 'react-native-modal';
import { Icon } from 'react-native-elements';
import { Ionicons, MaterialCommunityIcons, MaterialIcons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { styles } from 'styles/components/menuModal';
import useMediumScreen from 'hooks/useMediumScreen';
import useChangeAuthData from 'hooks/useChangeAuthData';
import MenuModalProps from 'types/components/MenuModalProps';
import UserRoleType from 'types/enums/UserRoleType';
import AppContext from 'contexts/AppContext';
import OrderContext from 'contexts/OrderContext';
import LanguageSelector from './LanguageSelector';
import { useTranslation } from 'react-i18next';

export default ({ visible }: MenuModalProps): JSX.Element => {
  const navigation = useNavigation();
  const { authData, setDisplayMenuModal, hideCloverMonitoring } = React.useContext(AppContext);
  const { currentVenue, currentCommerce } = React.useContext(OrderContext);
  const mediumScreen = useMediumScreen();
  const { deleteAuthData } = useChangeAuthData();
  const isVenueEditor = authData?.user.role === UserRoleType.VenueEditor;
  const isCommerceEditor = authData?.user.role === UserRoleType.CommerceEditor;
  const isFranchiseEditor = authData?.user.role === UserRoleType.FranchiseEditor;

  const { t } = useTranslation();

  const renderModal = (): JSX.Element => {
    if (!currentVenue || !authData) {
      return <></>;
    }
    return (
      <View style={mediumScreen ? styles.modalContentLarge : styles.modalContentSmall}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-around' }}>
          <View style={{ flexDirection: 'column', justifyContent: 'center', alignSelf: 'center' }}>
            <Text style={{ fontWeight: '700', fontSize: 20, lineHeight: 20, letterSpacing: 0.25, textAlign: 'right' }}>
              {currentVenue.name}
            </Text>
          </View>
          <Icon
            name="close"
            type="ionicon"
            color={'#001639'}
            size={35}
            containerStyle={styles.closeIconContainer}
            onPress={() => setDisplayMenuModal(false)}
          />
        </View>
        {mediumScreen && (
          <View style={{ flexDirection: 'column', justifyContent: 'center', marginVertical: 15 }}>
            <Icon name="person-circle-sharp" type="ionicon" size={60} />
            <Text
              style={{ textAlign: 'center', justifyContent: 'center', fontWeight: '400', lineHeight: 24, fontSize: 16 }}
            >{`${authData.user.email}`}</Text>
            <Text
              style={{ textAlign: 'center', justifyContent: 'center', fontWeight: '400', lineHeight: 24, fontSize: 16 }}
            >{`${authData.user.name}`}</Text>
          </View>
        )}
        <View>
          {(isVenueEditor || isFranchiseEditor) && (
            <Pressable
              onPress={() => {
                setDisplayMenuModal(false);
                navigation.navigate('CommercesScreen');
              }}
            >
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'flex-start',
                  alignContent: 'space-between',
                  marginVertical: 8,
                  marginHorizontal: 20,
                }}
              >
                <MaterialIcons
                  name="store"
                  color={currentVenue.appColor ?? '#000000'}
                  style={mediumScreen ? styles.iconListLarge : styles.iconListSmall}
                  size={mediumScreen ? 35 : 30}
                />
                <Text style={mediumScreen ? styles.modalTextLarge : styles.modalTextSmall}>{t('commerces')}</Text>
              </View>
            </Pressable>
          )}
          {isCommerceEditor && (
            <Pressable
              onPress={() => {
                setDisplayMenuModal(false);
                navigation.navigate('OrdersScreen');
              }}
            >
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'flex-start',
                  alignContent: 'space-between',
                  marginVertical: 8,
                  marginHorizontal: 20,
                }}
              >
                <MaterialCommunityIcons
                  name="order-bool-descending"
                  color={currentVenue.appColor ?? '#000000'}
                  style={mediumScreen ? styles.iconListLarge : styles.iconListSmall}
                  size={mediumScreen ? 35 : 30}
                />
                <Text style={mediumScreen ? styles.modalTextLarge : styles.modalTextSmall}>{t('orders')}</Text>
              </View>
            </Pressable>
          )}
          <Pressable
            onPress={() => {
              setDisplayMenuModal(false);
              navigation.navigate('ConsumptionsReportsScreen');
            }}
          >
            <View style={styles.itemContainer}>
              <MaterialCommunityIcons
                name="cart-arrow-down"
                type="ionicon"
                color={currentVenue.appColor ?? '#000000'}
                style={mediumScreen ? styles.iconListLarge : styles.iconListSmall}
                size={mediumScreen ? 35 : 30}
              />
              <Text style={mediumScreen ? styles.modalTextLarge : styles.modalTextSmall}>
                {t('consumptions-reports')}
              </Text>
            </View>
          </Pressable>
          <Pressable
            onPress={() => {
              setDisplayMenuModal(false);
              navigation.navigate('RefundsScreen');
            }}
          >
            <View style={styles.itemContainer}>
              <MaterialCommunityIcons
                name="cash-refund"
                type="ionicon"
                color={currentVenue.appColor ?? '#000000'}
                style={mediumScreen ? styles.iconListLarge : styles.iconListSmall}
                size={mediumScreen ? 35 : 30}
              />
              <Text style={mediumScreen ? styles.modalTextLarge : styles.modalTextSmall}>{t('refunds')}</Text>
            </View>
          </Pressable>
          {currentCommerce && (
            <Pressable
              onPress={() => {
                setDisplayMenuModal(false);
                navigation.navigate('VisualizationScreen');
              }}
            >
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'flex-start',
                  alignContent: 'space-between',
                  marginVertical: 8,
                  marginHorizontal: 20,
                }}
              >
                <MaterialIcons
                  name="ondemand-video"
                  size={mediumScreen ? 35 : 30}
                  style={mediumScreen ? styles.iconListLarge : styles.iconListSmall}
                />
                <Text style={mediumScreen ? styles.modalTextLarge : styles.modalTextSmall}>
                  {t('open-to-visualize')}
                </Text>
              </View>
            </Pressable>
          )}
          {!hideCloverMonitoring && (
            <Pressable
              onPress={() => {
                setDisplayMenuModal(false);
                navigation.navigate('CloverConnectionReportsScreen');
              }}
            >
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'flex-start',
                  alignContent: 'space-between',
                  marginVertical: 8,
                  marginHorizontal: 20,
                }}
              >
                <MaterialCommunityIcons
                  name="wifi"
                  color={currentVenue.appColor ?? '#000000'}
                  style={mediumScreen ? styles.iconListLarge : styles.iconListSmall}
                  size={mediumScreen ? 35 : 30}
                />
                <Text style={mediumScreen ? styles.modalTextLarge : styles.modalTextSmall}>{t('clover-connection-status.reports')}</Text>
              </View>
            </Pressable>
          )}
            
          <Pressable
            onPress={async () => {
              setDisplayMenuModal(false);
              await deleteAuthData();
            }}
          >
            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'flex-start',
                alignContent: 'space-between',
                marginVertical: 8,
                marginHorizontal: 20,
              }}
            >
              <Ionicons
                name="exit-outline"
                size={mediumScreen ? 35 : 30}
                color={'#000000'}
                style={{ marginRight: 20 }}
              />
              <Text style={mediumScreen ? styles.modalTextLarge : styles.modalTextSmall}>{t('exit')}</Text>
            </View>
          </Pressable>
        </View>
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignContent: 'space-between',
            marginHorizontal: 60,
          }}
        >
          <LanguageSelector />
        </View>
      </View>
    );
  };

  return (
    <Modal
      isVisible={visible}
      style={styles.modal}
      animationIn="slideInLeft"
      animationOut="slideOutLeft"
      hasBackdrop={true}
    >
      <View style={styles.modalBox}>{renderModal()}</View>
    </Modal>
  );
};
